import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  SITE_URL,
  DEFAULT_OG_IMAGE,
  absoluteUrl,
  getLocalizedPaths,
  type Lang,
} from '../lib/routes';

interface SEOMetaProps {
  title: string;
  description: string;
  ogImage?: string;
  ogType?: 'website' | 'article';
  noindex?: boolean;
}

export function SEOMeta({
  title,
  description,
  ogImage,
  ogType = 'website',
  noindex = false,
}: SEOMetaProps) {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();
  const lang: Lang = i18n.language === 'en' ? 'en' : 'fr';

  const alternates = getLocalizedPaths(pathname, lang);
  const canonical = absoluteUrl(pathname);
  const image = ogImage ? (ogImage.startsWith('http') ? ogImage : absoluteUrl(ogImage)) : DEFAULT_OG_IMAGE;
  const ogLocale = lang === 'fr' ? 'fr_FR' : 'en_US';

  return (
    <>
      <title>{title}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="fr" href={`${SITE_URL}${alternates.fr}`} />
      <link rel="alternate" hrefLang="en" href={`${SITE_URL}${alternates.en}`} />
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${alternates.fr}`} />

      <meta property="og:type" content={ogType} />
      <meta property="og:site_name" content="QBP.Solutions" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={image} />
      <meta property="og:locale" content={ogLocale} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
    </>
  );
}
